import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import userLookupService from '../../services/userLookup.services';
import { ValidateEmail } from '../../services/emailvalidations';

const hexRegex = /^[0-9A-Fa-f]{24}$/;

function ResultRow({ label, value, onCopy }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Typography sx={{ minWidth: 120, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{value}</Typography>
      <Tooltip title={`Copy ${label}`}>
        <IconButton size='small' onClick={() => onCopy(value, label)}>
          <ContentCopyIcon fontSize='inherit' />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function UserLookup() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailResult, setEmailResult] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [userId, setUserId] = useState('');
  const [userIdError, setUserIdError] = useState('');
  const [userIdResult, setUserIdResult] = useState(null);
  const [userIdLoading, setUserIdLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleCopy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnackbar({ open: true, message: `${label} copied to clipboard`, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Copy failed', severity: 'error' });
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value.trim();
    setEmail(value);
    setEmailError(value && !ValidateEmail(value) ? 'Please enter a valid email address.' : '');
  };

  const handleUserIdChange = (e) => {
    const value = e.target.value.trim();
    setUserId(value);
    setUserIdError(value && !hexRegex.test(value) ? 'User ID must be a 24-character hex string.' : '');
  };

  const searchByEmail = async () => {
    if (!ValidateEmail(email)) return;
    setEmailLoading(true);
    setEmailResult(null);
    try {
      const response = await userLookupService.getUserByEmail(email);
      if (response?.status === 'ok' && response.data?._id) {
        setEmailResult(response.data);
      } else {
        setSnackbar({ open: true, message: 'No user found for this email.', severity: 'info' });
      }
    } catch (err) {
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 404 ? 'No user found for this email.' : 'Lookup failed. Please try again.',
        severity: status === 404 ? 'info' : 'error',
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const searchByUserId = async () => {
    if (!hexRegex.test(userId)) return;
    setUserIdLoading(true);
    setUserIdResult(null);
    try {
      const response = await userLookupService.getUserById(userId);
      if (response?.status === 'ok' && response.data?._id) {
        setUserIdResult(response.data);
      } else {
        setSnackbar({ open: true, message: 'No user found for this user ID.', severity: 'info' });
      }
    } catch (err) {
      const status = err.response?.status;
      setSnackbar({
        open: true,
        message: status === 404 ? 'No user found for this user ID.' : 'Lookup failed. Please try again.',
        severity: status === 404 ? 'info' : 'error',
      });
    } finally {
      setUserIdLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant='h5' sx={{ mb: 3 }}>
        User Lookup
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          Email → User ID
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label='User Email'
            value={email}
            onChange={handleEmailChange}
            onKeyDown={(e) => e.key === 'Enter' && !emailError && searchByEmail()}
            error={!!emailError}
            helperText={emailError}
            size='small'
            fullWidth
          />
          <Button
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={searchByEmail}
            disabled={!email || !!emailError || emailLoading}
          >
            Search
          </Button>
        </Box>
        {emailLoading && <LinearProgress sx={{ mt: 2 }} />}
        {emailResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <ResultRow label='User ID' value={emailResult._id} onCopy={handleCopy} />
            <ResultRow label='Email' value={emailResult.email} onCopy={handleCopy} />
            <ResultRow
              label='Name'
              value={[emailResult.firstName, emailResult.lastName].filter(Boolean).join(' ')}
              onCopy={handleCopy}
            />
            <ResultRow label='Company ID' value={emailResult.companyId} onCopy={handleCopy} />
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant='h6' sx={{ mb: 2 }}>
          User ID → Email
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            label='User ID'
            value={userId}
            onChange={handleUserIdChange}
            onKeyDown={(e) => e.key === 'Enter' && !userIdError && searchByUserId()}
            error={!!userIdError}
            helperText={userIdError}
            size='small'
            fullWidth
          />
          <Button
            variant='contained'
            startIcon={<SearchIcon />}
            onClick={searchByUserId}
            disabled={!userId || !!userIdError || userIdLoading}
          >
            Search
          </Button>
        </Box>
        {userIdLoading && <LinearProgress sx={{ mt: 2 }} />}
        {userIdResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <ResultRow label='Email' value={userIdResult.email} onCopy={handleCopy} />
            <ResultRow label='User ID' value={userIdResult._id} onCopy={handleCopy} />
            <ResultRow
              label='Name'
              value={[userIdResult.firstName, userIdResult.lastName].filter(Boolean).join(' ')}
              onCopy={handleCopy}
            />
            <ResultRow label='Company ID' value={userIdResult.companyId} onCopy={handleCopy} />
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
